import { useEffect, type CSSProperties, type RefObject } from 'react';

export const GOOGLE_BUTTON_STYLE: CSSProperties = { colorScheme: 'light', minHeight: 44 };

type GoogleId = {
  accounts: {
    id: {
      initialize(o: {
        client_id: string;
        callback: (r: { credential: string }) => void;
        ux_mode?: string;
      }): void;
      renderButton(el: HTMLElement, o: Record<string, unknown>): void;
    };
  };
};
declare global {
  interface Window {
    google?: GoogleId;
  }
}

const loaded = new Map<string, Promise<void>>();
export function loadScript(src: string): Promise<void> {
  if (!loaded.has(src)) {
    loaded.set(
      src,
      new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => {
          loaded.delete(src);
          reject(new Error(`Failed to load ${src}`));
        };
        document.head.appendChild(s);
      }),
    );
  }
  return loaded.get(src)!;
}

const GSI_URL = 'https://accounts.google.com/gsi/client?hl=en';

/** Sign-in buttons share one size so they weigh the same: Google's large button is 40 px tall, and the
 * official Strava button (237 × 48) scaled to that height is ~198 px wide. */
export const SOCIAL_BUTTON_HEIGHT = 40;
export const SOCIAL_BUTTON_WIDTH = 198;

/**
 * Renders Google's own standard "Sign in with Google" button into `el` (Google's branding guidelines
 * ask for the SDK-rendered button): light theme, always in English: the Vietnamese wording is longer and
 * would make it wider than the Strava button. `onCredential` gets the ID token.
 * The host element needs `color-scheme: light` (GOOGLE_BUTTON_STYLE): under the app's dark scheme the
 * browser paints the button's iframe on an opaque white box, which showed as white corners.
 */
export function useGoogleButton(
  el: RefObject<HTMLDivElement | null>,
  clientId: string | null | undefined,
  onCredential: (credential: string) => void,
  onUnavailable: () => void,
  text: 'signin_with' | 'signup_with' | 'continue_with' = 'signin_with',
) {
  useEffect(() => {
    const node = el.current;
    if (!clientId || !node) return;
    loadScript(GSI_URL)
      .then(() => {
        window.google!.accounts.id.initialize({
          client_id: clientId,
          callback: (r) => onCredential(r.credential),
        });
        node.replaceChildren();
        window.google!.accounts.id.renderButton(node, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          logo_alignment: 'left',
          text,
          locale: 'en',
          width: SOCIAL_BUTTON_WIDTH,
        });
      })
      .catch(onUnavailable);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, text]);
}
